import { Dispatch, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setActions } from '../redux/general/sliceActionHandling';
import { IAppReduxStore } from '../redux/store';
import { getProjectDocumentsInfo } from '../redux/project/documents/sliceProjectDocuments';
import {
    ActionName,
    docTableActionsThunksMap as actionsDependencies,
} from '../components/General/DocTable/DocTableActions';
import { downloadRedliningReport } from '../redux/project/documents/sliceProjectReportTemplate';
import { fetchExcelTemplates } from '../redux/project/sliceProjectExcelUpload';
import {
    getProjectDocTypeIndices,
    getProjectDocTypeInfo,
} from '../redux/project/sliceProjectDocTypes';
import { getLinkedPartialModels } from '../redux/project/documents/sliceProjectAssembly';
import { getDocNumberConvention } from '../redux/import/sliceDocnumberSidepanel';
import { onAdvancedExportDocuments } from '../redux/project/documents/sliceProjectAdvancedExportDialog';
import { exportDocumentsToCSV } from '../redux/project/documents/sliceProjectDocumentsExportToCSV';
import { getProjectDocumentDetails } from '../redux/project/documents/sliceProjectDocumentDetails';
import { getAdditionalLinkDocument } from '../redux/project/documents/sliceProjectAdditionalDocuments';
import { checkDocumentFile, openDocumentFile } from '../redux/project/file/sliceProjectFileOpen';
import { openMultipleDocuments } from '../redux/project/documents/sliceProjectDocumentsOpenMultiple';
import { getProjectDocumentsFiles } from '../redux/project/documents/sliceProjectDocumentsFiles';
import {
    downloadDocument,
    downloadDocuments,
} from '../redux/project/file/sliceProjectFileDownload';
import {
    runDynamicAction,
    runFormdesignersDynamicAction,
} from '../redux/project/documents/sliceProjectDocumentsDynamicSelection';
import { Thunk } from '../types';

export default () => {
    const dispatch: Dispatch<unknown> = useDispatch();
    const [activeThunks, setActiveThunks] = useState<string[]>([]);

    const redliningReportThunkPending = useSelector(
        (store: IAppReduxStore) => store.projectDocumentsReportFromTemplate.redliningReportState,
    );
    const documentInfoThunkPending = useSelector(
        (store: IAppReduxStore) => store.projectDocuments.info.loading,
    );
    const excelTemplatesThunkPending = useSelector(
        (store: IAppReduxStore) => store.excelUpload.loading,
    );
    const doctypeIndicesThunkPending = useSelector(
        (store: IAppReduxStore) => store.projectDocTypes.indicesLoading,
    );
    const doctypeInfoThunkPending = useSelector(
        (store: IAppReduxStore) => store.projectDocTypes.docTypeInfoState,
    );
    const linkedPartialModelsThunkPending = useSelector(
        (store: IAppReduxStore) => store.projectSaveAssembly.linkedPartialModels.loading,
    );
    const docnumberConventionThunkPending = useSelector(
        (store: IAppReduxStore) => store.docnumberSidepanel.loading,
    );
    const advancedExportThunkPending = useSelector(
        (store: IAppReduxStore) => store.projectAdvancedExportDialog.loading,
    );
    const exportCsvThunkPending = useSelector(
        (store: IAppReduxStore) => store.projectDocumentsExportToCSV.loading,
    );
    const documentDetailsThunkPending = useSelector(
        (store: IAppReduxStore) => store.projectDocumentDetails.loading,
    );
    const linkDocumentThunkPending = useSelector(
        (store: IAppReduxStore) => store.projectAdditionalDocs.linkDocumentUrlPending,
    );
    const fileCheckThunkPending = useSelector(
        (store: IAppReduxStore) => store.projectFileOpen.checkLoading,
    );
    const fileOpenThunkPending = useSelector(
        (store: IAppReduxStore) => store.projectFileOpen.loading,
    );
    const openMultipleThunkPending = useSelector(
        (store: IAppReduxStore) => store.projectDocumentsOpenMultiple.loading,
    );
    const documentsFilesThunkPending = useSelector(
        (store: IAppReduxStore) => store.projectDocumentsFiles.loading,
    );
    const downloadDocumentThunkPending = useSelector(
        (store: IAppReduxStore) => store.projectFileDownload.loadingSingleDocument,
    );
    const downloadDocumentsThunkPending = useSelector(
        (store: IAppReduxStore) => store.projectFileDownload.downloadDocuments.loading,
    );
    const dynamicActionThunkPending = useSelector(
        (store: IAppReduxStore) => store.projectDocumentsDynamicSelection.loadingRunDynamicAction,
    );
    const formDesignerActionThunkPending = useSelector(
        (store: IAppReduxStore) =>
            store.projectDocumentsDynamicSelection.loadingFormDesignerDynamicAction,
    );

    const onThunkPendingChanged = (isThunkPending: boolean, thunk: Thunk) => {
        if (isThunkPending && !activeThunks.includes(thunk.typePrefix)) {
            setActiveThunks(prev => [...prev, thunk.typePrefix]);
        } else if (activeThunks.includes(thunk.typePrefix)) {
            setActiveThunks(prev => prev.filter(x => x !== thunk.typePrefix));
        }
    };

    useEffect(() => {
        const dependantActions = (Object.keys(actionsDependencies) as ActionName[]).filter(action =>
            actionsDependencies[action].some(thunk => activeThunks.includes(thunk?.typePrefix)),
        );

        dispatch(setActions(dependantActions));
    }, [activeThunks]);

    useEffect(() => {
        onThunkPendingChanged(redliningReportThunkPending === 'Pending', downloadRedliningReport);
    }, [redliningReportThunkPending]);

    useEffect(() => {
        onThunkPendingChanged(doctypeInfoThunkPending === 'Pending', getProjectDocTypeInfo);
    }, [doctypeInfoThunkPending]);

    useEffect(() => {
        onThunkPendingChanged(documentInfoThunkPending, getProjectDocumentsInfo);
    }, [documentInfoThunkPending]);

    useEffect(() => {
        onThunkPendingChanged(excelTemplatesThunkPending, fetchExcelTemplates);
    }, [excelTemplatesThunkPending]);

    useEffect(() => {
        onThunkPendingChanged(doctypeIndicesThunkPending, getProjectDocTypeIndices);
    }, [doctypeIndicesThunkPending]);

    useEffect(() => {
        onThunkPendingChanged(linkedPartialModelsThunkPending, getLinkedPartialModels);
    }, [linkedPartialModelsThunkPending]);

    useEffect(() => {
        onThunkPendingChanged(docnumberConventionThunkPending, getDocNumberConvention);
    }, [docnumberConventionThunkPending]);

    useEffect(() => {
        onThunkPendingChanged(advancedExportThunkPending, onAdvancedExportDocuments);
    }, [advancedExportThunkPending]);

    useEffect(() => {
        onThunkPendingChanged(exportCsvThunkPending, exportDocumentsToCSV);
    }, [exportCsvThunkPending]);

    useEffect(() => {
        onThunkPendingChanged(documentDetailsThunkPending, getProjectDocumentDetails);
    }, [documentDetailsThunkPending]);

    useEffect(() => {
        onThunkPendingChanged(linkDocumentThunkPending, getAdditionalLinkDocument);
    }, [linkDocumentThunkPending]);

    useEffect(() => {
        onThunkPendingChanged(fileCheckThunkPending, checkDocumentFile);
    }, [fileCheckThunkPending]);

    useEffect(() => {
        onThunkPendingChanged(fileOpenThunkPending, openDocumentFile);
    }, [fileOpenThunkPending]);

    useEffect(() => {
        onThunkPendingChanged(openMultipleThunkPending, openMultipleDocuments);
    }, [openMultipleThunkPending]);

    useEffect(() => {
        onThunkPendingChanged(documentsFilesThunkPending, getProjectDocumentsFiles);
    }, [documentsFilesThunkPending]);

    useEffect(() => {
        onThunkPendingChanged(downloadDocumentThunkPending, downloadDocument);
    }, [downloadDocumentThunkPending]);

    useEffect(() => {
        onThunkPendingChanged(downloadDocumentsThunkPending, downloadDocuments);
    }, [downloadDocumentsThunkPending]);

    useEffect(() => {
        onThunkPendingChanged(dynamicActionThunkPending, runDynamicAction);
    }, [dynamicActionThunkPending]);

    useEffect(() => {
        onThunkPendingChanged(formDesignerActionThunkPending, runFormdesignersDynamicAction);
    }, [formDesignerActionThunkPending]);
};
